import { useState, useEffect } from 'react';
import {
  ShoppingCart,
  Package,
  Truck,
  TrendingUp,
  ScanBarcode,
  Warehouse,
  BarChart3,
  Tag,
  Clock,
  ArrowRight,
  ChevronRight,
  Activity,
  Calculator,
} from 'lucide-react';
import { EmptyState, SkeletonStatTile, PullToRefresh } from '../../components/boothly/index.js';
import { HeuteBlock } from '../../dashboard/heute-block.jsx';
import { fmtCurrency } from '../../lib/format.js';

export function Dashboard({
  store,
  currentRole,
  dark,
  onOpenLager,
  onOpenStats,
  onOpenCatalog,
  onOpenCalculator,
  onOpenNew,
  onSwitchToScanner,
  onSwitchToBestellungen,
  onRefresh,
}) {
  // Katalog/Preis-Pflege nur owner/admin (manage_catalog) — Helfer sehen die Kachel nicht.
  const canManageCatalog = ['owner', 'admin'].includes(currentRole);
  useEffect(() => {
    if (!store.auditEntries?.length) {
      store.loadAuditLog?.();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const orders = store.orders || [];
  const counts = store.counts || {};

  // counts has: Alle, Bereit, Versendet, Retoure — compute Offen separately
  const offenCount    = orders.filter(o => o.status === 'Offen').length;
  const bereitCount   = counts.Bereit ?? 0;
  const versendetCount = counts.Versendet ?? 0;
  const revenue       = store.revenue ?? 0;

  const revenueFormatted = fmtCurrency(revenue || 0);

  const auditEntries = (store.auditEntries || []).slice(0, 5);

  const isLoading = !store.loaded;

  // Letzte Aktivitäten: standardmäßig zugeklappt (ruhigere Übersicht).
  const [activitiesOpen, setActivitiesOpen] = useState(false);

  // HEUTE-Block (gleiche Logik wie Start-Screen, aber nur dieser Shop):
  // offen = Status Offen ODER Bereit. storeName weggelassen (Einzel-Shop redundant).
  const heuteOrders = orders
    .filter(o => o.status === 'Offen' || o.status === 'Bereit')
    .slice(0, 2)
    .map(o => ({ id: o.id, bestellnummer: o.bestellnummer || o.id, status: o.status }));
  const heuteCount = offenCount + bereitCount;

  return (
    <PullToRefresh onRefresh={onRefresh}>
      <div data-testid="screen-dashboard" className="space-y-6 px-5 pt-4" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
      {/* 1) HEUTE — ganz oben, gefiltert auf nur diesen Shop (gleiche Optik wie Start-Screen) */}
      {isLoading ? (
        <div className="bg-[var(--bg-L1)] border border-[var(--border-default)] rounded-2xl h-20 animate-pulse" />
      ) : (
        <HeuteBlock
          liveSession={null}
          openOrders={heuteOrders}
          openOrdersCount={heuteCount}
          onOpenStore={() => onSwitchToBestellungen()}
          onOpenLive={() => {}}
          flat
        />
      )}

      {/* 2) UEBERBLICK — 2×2 Stat-Kacheln. Offen/Bereit mit Handlungs-Signal
          (gruen bei 0, gold bei >0) — NICHT die Status-Farbe. Versendet/Umsatz neutral. */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonStatTile key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <StatCard testid="stat-tile-offen"     label="Offen"     value={offenCount}        icon={ShoppingCart} signal={offenCount === 0 ? 'ok' : 'warn'} />
          <StatCard testid="stat-tile-bereit"    label="Bereit"    value={bereitCount}       icon={Package}      signal={bereitCount === 0 ? 'ok' : 'warn'} />
          <StatCard testid="stat-tile-versendet" label="Versendet" value={versendetCount}    icon={Truck}        color="green" />
          <StatCard testid="stat-tile-umsatz"    label="Umsatz"    value={revenueFormatted}  icon={TrendingUp}   color="blue" />
        </div>
      )}

      {/* 3) Quick Actions — "Neue Bestellung" entfaellt (jetzt schwebender Button im Bestellungen-Tab) */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Schnellzugriff
        </h3>
        <div className="flex divide-x divide-border/50 overflow-hidden rounded-2xl border border-[var(--border-default)] bg-card">
          <QuickActionItem testid="quick-action-scanner" icon={ScanBarcode} label="Scanner" onClick={onSwitchToScanner} />
          <QuickActionItem testid="quick-action-lager"   icon={Warehouse}   label="Lager"   onClick={onOpenLager} />
          <QuickActionItem testid="quick-action-stats"   icon={BarChart3}   label="Stats"   onClick={onOpenStats} />
          {canManageCatalog && (
            <QuickActionItem testid="quick-action-katalog" icon={Tag} label="Katalog" onClick={onOpenCatalog} />
          )}
        </div>
      </div>

      {/* 4) Preis-Kalkulator — unter dem Schnellzugriff, flach (ohne Karten-Hintergrund) */}
      <button
        data-testid="card-preis-kalkulator"
        onClick={onOpenCalculator}
        className="flex w-full items-center gap-4 py-2 text-left transition-transform active:scale-[0.99]"
      >
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-ios-blue/[0.16]">
          <Calculator className="h-5 w-5 text-ios-blue" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold">Preis-Kalkulator</p>
          <p className="text-sm text-muted-foreground">Gewinn & Marge durchrechnen</p>
        </div>
        <ArrowRight className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
      </button>

      {/* 5) Letzte Aktivitäten — Tagline mit Chevron, standardmäßig zugeklappt, flach */}
      <div>
        <button
          onClick={() => setActivitiesOpen(o => !o)}
          data-testid="recent-activities-toggle"
          aria-expanded={activitiesOpen}
          className="flex w-full items-center justify-between"
        >
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Letzte Aktivitäten
          </span>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground"
            style={{ transform: activitiesOpen ? 'rotate(90deg)' : 'none', transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)' }}
          />
        </button>
        {activitiesOpen && (
          <div data-testid="recent-activities" className="mt-3 divide-y divide-border/50">
            {isLoading ? (
              <div className="space-y-3 py-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded-full bg-[var(--fill-tertiary)] animate-pulse flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-4 w-3/4 rounded bg-[var(--fill-tertiary)] animate-pulse" />
                    </div>
                    <div className="h-3 w-12 rounded bg-[var(--fill-tertiary)] animate-pulse" />
                  </div>
                ))}
              </div>
            ) : auditEntries.length === 0 ? (
              <EmptyState
                variant="compact"
                icon={Activity}
                title="Noch keine Aktivitäten"
                description="Hier erscheinen kürzliche Änderungen an deinen Bestellungen."
              />
            ) : (
              auditEntries.map((entry, i) => {
                const { icon, text } = store.describeAuditEntry(entry);
                const time = store.formatRelative(entry.created_at);
                return (
                  <ActivityItem
                    key={entry.id || i}
                    icon={icon}
                    text={text}
                    time={time}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
    </PullToRefresh>
  );
}

function StatCard({ label, value, icon: Icon, color, signal, testid }) {
  const colorClasses = {
    blue:   'text-ios-blue bg-ios-blue/[0.16]',
    green:  'text-ios-green bg-ios-green/[0.16]',
    purple: 'text-ios-purple bg-ios-purple/[0.16]',
  };

  // Handlungs-Signal (nur Offen/Bereit): 'ok' = gruen (0), 'warn' = gold (>0).
  // Bewusst NICHT die Status-Farbe — Offen bleibt blau, Bereit lila in Listen/Pills.
  const sig = signal === 'warn'
    ? { bg: 'var(--tile-warn-bg)', bd: 'var(--tile-warn-bd)', num: 'var(--tile-warn-num)' }
    : signal === 'ok'
      ? { bg: 'var(--tile-ok-bg)', bd: 'var(--tile-ok-bd)', num: 'var(--tile-ok-num)' }
      : null;

  return (
    <div
      data-testid={testid}
      className="glass-card glass-shine rounded-2xl p-4"
      style={sig ? { background: sig.bg, borderColor: sig.bd } : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          {/* Offen/Bereit (sig): groesser/fetter — Handlungsbedarf. Versendet/Umsatz unveraendert. */}
          {sig ? (
            <p className="mt-1 text-4xl font-extrabold tabular-nums tracking-tight" style={{ color: sig.num, lineHeight: 1 }}>{value}</p>
          ) : (
            <p className="mt-1 text-2xl font-bold">{value}</p>
          )}
        </div>
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full ${sig ? '' : colorClasses[color]}`}
          style={sig ? { background: sig.bd, color: sig.num } : undefined}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

// Eine Aktion in der vereinheitlichten Schnellzugriff-Karte (nebeneinander, Trenner via divide-x).
function QuickActionItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex flex-1 flex-col items-center gap-1.5 py-4 transition-opacity active:opacity-70"
    >
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span className="text-center text-[0.6875rem] font-medium leading-tight">
        {label}
      </span>
    </button>
  );
}

function ActivityItem({ icon, text, time }) {
  return (
    <div data-testid="activity-item" className="flex items-center gap-3 px-0 py-3">
      <span className="text-base leading-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{text}</p>
      </div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
        <Clock className="h-3 w-3" />
        {time}
      </div>
    </div>
  );
}
